import { useCallback, useEffect, useRef, useState } from 'react'
import { AUDIO_MAX_DURATION_SEC } from '@yui-receipt/shared'
import { pickRecorderMime } from '@/lib/voicesoap/mime'
import { decodeBlobDurationSec, formatMmSs } from '@/lib/voicesoap/duration'
import { WakeLockGuard } from '@/lib/voicesoap/wakeLock'
import { toastError, toastInfo } from '@/store/toast'
import Waveform from './Waveform'

export type RecorderResult = {
  blob: Blob
  mimeType: string
  durationSec: number
  startedAt: number
}

type Props = {
  /** Fired once when the user stops recording and we've computed a real duration. */
  onComplete: (r: RecorderResult) => void
  /** Disables the primary button (e.g. while uploading). */
  disabled?: boolean
  /** Called whenever the recorder enters a new state — useful for parent analytics. */
  onStateChange?: (state: RecorderState) => void
}

export type RecorderState =
  | 'idle'
  | 'requesting'
  | 'recording'
  | 'paused'
  | 'stopping'
  | 'error'

const TIMER_TICK_MS = 250

export default function Recorder({ onComplete, disabled, onStateChange }: Props) {
  const [state, setState] = useState<RecorderState>('idle')
  const [elapsedSec, setElapsedSec] = useState(0)
  const [errorMsg, setErrorMsg] = useState<string | null>(null)
  const [stream, setStream] = useState<MediaStream | null>(null)

  const mediaRef = useRef<MediaRecorder | null>(null)
  const chunksRef = useRef<Blob[]>([])
  const startedAtRef = useRef<number>(0)
  const pausedAccumRef = useRef<number>(0)
  const pausedAtRef = useRef<number>(0)
  const timerRef = useRef<number | null>(null)
  const wakeLockRef = useRef<WakeLockGuard>(new WakeLockGuard())
  const mimeRef = useRef<string | undefined>(undefined)
  const completedRef = useRef(false)
  // `state` captured inside visibility handlers goes stale — ref keeps current.
  const stateRef = useRef<RecorderState>('idle')
  const autoPausedRef = useRef(false)

  useEffect(() => {
    stateRef.current = state
    onStateChange?.(state)
  }, [state, onStateChange])

  const stopTimer = useCallback(() => {
    if (timerRef.current != null) {
      window.clearInterval(timerRef.current)
      timerRef.current = null
    }
  }, [])

  const computeElapsed = useCallback(() => {
    if (!startedAtRef.current) return 0
    const paused =
      pausedAtRef.current > 0
        ? pausedAccumRef.current + (Date.now() - pausedAtRef.current)
        : pausedAccumRef.current
    return Math.max(0, (Date.now() - startedAtRef.current - paused) / 1000)
  }, [])

  const startTimer = useCallback(() => {
    stopTimer()
    timerRef.current = window.setInterval(() => {
      const e = computeElapsed()
      setElapsedSec(e)
      if (e >= AUDIO_MAX_DURATION_SEC) {
        toastInfo('ครบ 30 นาที — หยุดอัดอัตโนมัติ')
        void handleStop()
      }
    }, TIMER_TICK_MS)
    setElapsedSec(computeElapsed())
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [computeElapsed, stopTimer])

  const cleanupStream = useCallback(() => {
    setStream((s) => {
      s?.getTracks().forEach((t) => t.stop())
      return null
    })
    mediaRef.current = null
  }, [])

  const resetState = useCallback(() => {
    stopTimer()
    chunksRef.current = []
    startedAtRef.current = 0
    pausedAccumRef.current = 0
    pausedAtRef.current = 0
    completedRef.current = false
    autoPausedRef.current = false
    setElapsedSec(0)
  }, [stopTimer])

  // ---------------------------------------------------------------------------
  // Start
  // ---------------------------------------------------------------------------
  async function handleStart() {
    if (state !== 'idle' && state !== 'error') return
    setErrorMsg(null)
    setState('requesting')
    try {
      const s = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
        video: false,
      })
      setStream(s)
      const mime = pickRecorderMime()
      mimeRef.current = mime
      const mr = mime ? new MediaRecorder(s, { mimeType: mime }) : new MediaRecorder(s)
      mediaRef.current = mr
      chunksRef.current = []
      mr.addEventListener('dataavailable', (e) => {
        if (e.data && e.data.size > 0) chunksRef.current.push(e.data)
      })
      mr.addEventListener('error', (e) => {
        // eslint-disable-next-line no-console
        console.error('[recorder] error', e)
        setErrorMsg('เกิดข้อผิดพลาดระหว่างอัดเสียง — ลองใหม่อีกครั้ง')
        setState('error')
        cleanupStream()
        void wakeLockRef.current.release()
        resetState()
      })
      mr.addEventListener('stop', handleRecorderStop)
      mr.start(1000)
      startedAtRef.current = Date.now()
      pausedAccumRef.current = 0
      pausedAtRef.current = 0
      setState('recording')
      startTimer()
      void wakeLockRef.current.acquire()
      wakeLockRef.current.armReacquireOnVisible()
    } catch (err) {
      cleanupStream()
      setState('error')
      const msg =
        err instanceof DOMException && err.name === 'NotAllowedError'
          ? 'กรุณาอนุญาต microphone permission เพื่ออัดเสียง'
          : err instanceof DOMException && err.name === 'NotFoundError'
            ? 'ไม่พบอุปกรณ์ไมโครโฟน'
            : 'เปิดไมโครโฟนไม่สำเร็จ — ลองรีเฟรชหน้าเว็บ'
      setErrorMsg(msg)
      toastError(msg)
    }
  }

  // ---------------------------------------------------------------------------
  // Pause / Resume
  // ---------------------------------------------------------------------------
  function handlePause(auto = false) {
    const mr = mediaRef.current
    if (!mr || stateRef.current !== 'recording') return
    try {
      mr.pause()
      pausedAtRef.current = Date.now()
      stopTimer()
      setState('paused')
      if (auto) autoPausedRef.current = true
      void wakeLockRef.current.release()
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('[recorder] pause failed', e)
    }
  }

  function handleResume() {
    const mr = mediaRef.current
    if (!mr || stateRef.current !== 'paused') return
    try {
      mr.resume()
      if (pausedAtRef.current > 0) {
        pausedAccumRef.current += Date.now() - pausedAtRef.current
        pausedAtRef.current = 0
      }
      autoPausedRef.current = false
      setState('recording')
      startTimer()
      void wakeLockRef.current.acquire()
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('[recorder] resume failed', e)
    }
  }

  // ---------------------------------------------------------------------------
  // Stop
  // ---------------------------------------------------------------------------
  async function handleStop() {
    const mr = mediaRef.current
    if (!mr || (stateRef.current !== 'recording' && stateRef.current !== 'paused')) return
    setState('stopping')
    stopTimer()
    try {
      mr.stop()
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('[recorder] stop failed', e)
    }
  }

  async function handleRecorderStop() {
    if (completedRef.current) return
    completedRef.current = true
    if (pausedAtRef.current > 0) {
      pausedAccumRef.current += Date.now() - pausedAtRef.current
      pausedAtRef.current = 0
    }
    await wakeLockRef.current.release()
    const chunks = chunksRef.current.slice()
    const mime = mimeRef.current ?? mediaRef.current?.mimeType ?? 'audio/webm'
    cleanupStream()
    if (chunks.length === 0) {
      setState('error')
      setErrorMsg('ไม่ได้รับข้อมูลเสียง — ลองอัดใหม่อีกครั้ง')
      resetState()
      return
    }
    const blob = new Blob(chunks, { type: mime })
    let durationSec = await decodeBlobDurationSec(blob)
    if (!durationSec || !Number.isFinite(durationSec)) durationSec = computeElapsed()
    const started = startedAtRef.current
    onComplete({ blob, mimeType: mime, durationSec, startedAt: started })
    resetState()
    setState('idle')
  }

  // ---------------------------------------------------------------------------
  // visibilitychange: auto-pause when backgrounded, notify on return
  // ---------------------------------------------------------------------------
  useEffect(() => {
    const onVis = () => {
      void wakeLockRef.current.handleVisibilityChange()
      if (document.visibilityState === 'hidden') {
        if (stateRef.current === 'recording') {
          handlePause(true)
        }
      } else if (document.visibilityState === 'visible') {
        if (autoPausedRef.current && stateRef.current === 'paused') {
          toastInfo('หยุดอัดชั่วคราวตอนหน้าจอถูกซ่อน — แตะ "อัดต่อ" เพื่ออัดต่อ')
        }
      }
    }
    document.addEventListener('visibilitychange', onVis)
    return () => document.removeEventListener('visibilitychange', onVis)
  }, [])

  useEffect(() => {
    return () => {
      stopTimer()
      cleanupStream()
      void wakeLockRef.current.release()
    }
  }, [cleanupStream, stopTimer])

  const isRecording = state === 'recording'
  const isPaused = state === 'paused'
  const isBusy = state === 'requesting' || state === 'stopping'
  const canStart = (state === 'idle' || state === 'error') && !disabled
  const canPauseOrResume = isRecording || isPaused
  const canStop = isRecording || isPaused

  return (
    <div className="card flex flex-col items-center gap-4">
      <div className="sr-only" role="status" aria-live="polite">
        {stateLabel(state)} {isRecording || isPaused ? `${formatMmSs(elapsedSec)}` : ''}
      </div>

      {(isRecording || isPaused) && (
        <div
          className="w-full rounded-lg bg-amber-50 border border-amber-300 text-amber-900 px-4 py-3 text-sm font-medium text-center"
          role="alert"
        >
          ⚠️ อย่าล็อกหน้าจอระหว่างอัดเสียง — ให้แอปทำงานค้างไว้
        </div>
      )}

      {/* Live waveform — visible only while stream exists. */}
      {stream && (
        <Waveform stream={stream} active={isRecording} className="w-full rounded-md bg-slate-100" />
      )}

      <button
        type="button"
        className={
          'inline-flex items-center justify-center rounded-full h-28 w-28 shadow-lg transition ' +
          'active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed ' +
          (isRecording
            ? 'bg-rose-600 hover:bg-rose-700 text-white ring-4 ring-rose-200 animate-pulse'
            : isPaused
              ? 'bg-amber-500 hover:bg-amber-600 text-white'
              : 'bg-brand-600 hover:bg-brand-700 text-white')
        }
        onClick={() => {
          if (canStart) void handleStart()
          else if (canStop) void handleStop()
        }}
        disabled={isBusy || (!canStart && !canStop)}
        aria-label={canStart ? 'เริ่มอัดเสียง' : isBusy ? 'กำลังประมวลผล' : 'หยุดอัดเสียง'}
      >
        {isBusy ? (
          <span className="text-sm">กำลัง…</span>
        ) : canStart ? (
          <span className="text-4xl" aria-hidden>🎙️</span>
        ) : (
          <span className="h-10 w-10 bg-white rounded-md" aria-hidden />
        )}
      </button>

      <div
        className="font-mono text-4xl tabular-nums tracking-wide"
        aria-hidden={!isRecording && !isPaused}
      >
        {formatMmSs(elapsedSec)}
      </div>

      <div className="flex gap-3">
        {isRecording && (
          <button
            type="button"
            className="btn-secondary"
            onClick={() => handlePause(false)}
            aria-label="พักการอัด"
          >
            ⏸ พัก
          </button>
        )}
        {isPaused && (
          <button
            type="button"
            className="btn-primary"
            onClick={handleResume}
            aria-label="อัดต่อ"
          >
            ▶ อัดต่อ
          </button>
        )}
        {canPauseOrResume && (
          <button
            type="button"
            className="btn-danger"
            onClick={() => void handleStop()}
            aria-label="หยุดและบันทึก"
          >
            ⏹ หยุด
          </button>
        )}
      </div>

      {state === 'idle' && (
        <p className="text-sm text-slate-500 text-center max-w-md">
          คลินิกใช้เสียงของคุณเพื่อสรุปบันทึก SOAP เท่านั้น ไม่เผยแพร่
          — แตะปุ่มไมค์เพื่อเริ่มอัด
        </p>
      )}
      {state === 'error' && errorMsg && (
        <p className="text-sm text-rose-600 text-center" role="alert">
          {errorMsg}
        </p>
      )}
      {state === 'requesting' && (
        <p className="text-sm text-slate-500 text-center">กำลังขอสิทธิ์ไมโครโฟน…</p>
      )}
      {state === 'stopping' && (
        <p className="text-sm text-slate-500 text-center">กำลังบันทึกไฟล์เสียง…</p>
      )}
    </div>
  )
}

function stateLabel(s: RecorderState): string {
  switch (s) {
    case 'idle':
      return 'พร้อมเริ่มอัดเสียง'
    case 'requesting':
      return 'ขอสิทธิ์ไมโครโฟน'
    case 'recording':
      return 'กำลังอัดเสียง'
    case 'paused':
      return 'พักการอัดเสียง'
    case 'stopping':
      return 'กำลังบันทึกไฟล์'
    case 'error':
      return 'มีข้อผิดพลาด'
  }
}
